import React, { useEffect, useMemo, useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Home,
  UserCog,
  Heart,
  Ticket,
  Crown,
  Trophy,
  Film,
  Radio,
  Download,
  Award,
  LogOut,
  ShieldCheck,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { DASHBOARD } from '@/constants/testIds';
import { ROUTES } from '@/lib/constants';
import { fetchDashboardMe, fetchMyPlayerProfile, friendlyError } from '@/lib/api';
import { toast } from 'sonner';

import { OverviewTab } from '@/components/dashboard/tabs/OverviewTab';
import { ProfileTab } from '@/components/dashboard/tabs/ProfileTab';
import { MyClubTab } from '@/components/dashboard/tabs/MyClubTab';
import { MyPurchasesTab } from '@/components/dashboard/tabs/MyPurchasesTab';
import { MySubscriptionsTab } from '@/components/dashboard/tabs/MySubscriptionsTab';
import { MyEventsTab } from '@/components/dashboard/tabs/MyEventsTab';
import { MyVideosTab } from '@/components/dashboard/tabs/MyVideosTab';
import { MyLivesTab } from '@/components/dashboard/tabs/MyLivesTab';
import { MyDownloadsTab } from '@/components/dashboard/tabs/MyDownloadsTab';
import { PlayerProfileTab } from '@/components/dashboard/tabs/PlayerProfileTab';

const BASE_TABS = [
  { key: 'overview', label: 'Inicio', icon: Home },
  { key: 'profile', label: 'Mi perfil', icon: UserCog },
  // "Mi perfil deportivo" solo para profile_type === 'player' o usuarios con ficha vinculada.
  // Dentro se muestra el bloque "Mis highlights oficiales" (Fase 09).
  { key: 'player', label: 'Mi perfil deportivo', icon: Award, playerOnly: true },
  { key: 'club', label: 'Mi club', icon: Heart },
  { key: 'purchases', label: 'Mis compras', icon: Ticket },
  { key: 'subscriptions', label: 'Mis suscripciones', icon: Crown },
  { key: 'events', label: 'Mis partidos', icon: Trophy },
  { key: 'videos', label: 'Mis vídeos', icon: Film },
  // "Mis directos" debe ir DESPUÉS de eventos y vídeos (requerido por producto)
  { key: 'lives', label: 'Mis directos', icon: Radio },
  { key: 'downloads', label: 'Mis descargas', icon: Download },
];

// Fase 09 · hash especial para saltar directamente a la sección Mis
// highlights oficiales que vive DENTRO de la pestaña "Mi perfil deportivo".
const HIGHLIGHTS_ANCHOR_HASH = 'player-profile-highlights';
const HIGHLIGHTS_ANCHOR_ID = 'mis-highlights-oficiales';
// Alias legacy: notificaciones anteriores pueden traer #highlights.
const LEGACY_HIGHLIGHTS_HASHES = new Set(['player-profile-highlights', 'highlights']);

function readTabFromHash() {
  if (typeof window === 'undefined') return 'overview';
  const h = (window.location.hash || '').replace(/^#/, '').trim();
  if (!h) return 'overview';
  // El hash de highlights redirige a la pestaña "player"; el scroll se
  // gestiona en un efecto aparte.
  if (LEGACY_HIGHLIGHTS_HASHES.has(h)) return 'player';
  return h;
}

export default function Dashboard() {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(readTabFromHash);
  const [hasLinkedPlayerProfile, setHasLinkedPlayerProfile] = useState(false);

  // Sync hash <-> active tab (soporta tanto hashchange nativo como cambios
  // programáticos vía react-router-dom <Link to="/dashboard#...">).
  useEffect(() => {
    setActive(readTabFromHash());
  }, [location.hash]);
  useEffect(() => {
    const onHash = () => setActive(readTabFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // Fase 09 · Cuando el hash es el ancla de highlights, hacer scroll suave
  // hasta la sección DENTRO de "Mi perfil deportivo". Se reintenta con
  // pequeño delay porque la pestaña se pinta al cambiar `active`.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const hash = (location.hash || '').replace(/^#/, '').trim();
    if (!LEGACY_HIGHLIGHTS_HASHES.has(hash)) return undefined;
    if (active !== 'player') return undefined;
    const doScroll = () => {
      const el = document.getElementById(HIGHLIGHTS_ANCHOR_ID);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    const t1 = setTimeout(doScroll, 250);
    const t2 = setTimeout(doScroll, 900);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [active, location.hash]);

  const goTab = (key) => {
    setActive(key);
    if (typeof window !== 'undefined') {
      window.location.hash = key;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await fetchDashboardMe();
        if (alive) setSummary(data);
      } catch (err) {
        if (alive) toast.error(friendlyError(err));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Fase 09 — Resolver si el usuario tiene ficha deportiva vinculada.
  // La relación canónica es player_profiles.user_id === users.id.
  // La pestaña "Mis highlights oficiales" y "Mi perfil deportivo" dependen
  // de que exista dicha vinculación (has_linked_profile) o de que el usuario
  // sea profile_type=player aún sin ficha (para poder ver el mensaje del club).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const env = await fetchMyPlayerProfile();
        if (!alive) return;
        setHasLinkedPlayerProfile(Boolean(env?.has_linked_profile || env?.profile?.id));
      } catch {
        if (alive) setHasLinkedPlayerProfile(false);
      }
    })();
    return () => { alive = false; };
  }, [user?.id]);

  const tabs = useMemo(() => {
    const isPlayer = user?.profile_type === 'player';
    return BASE_TABS.filter((t) => {
      if (t.requiresLinkedProfile) return hasLinkedPlayerProfile;
      if (t.playerOnly) return isPlayer || hasLinkedPlayerProfile;
      return true;
    });
  }, [user?.profile_type, hasLinkedPlayerProfile]);

  // Si el tab actual ya no es válido (p.ej. usuario no-player tras carga), volver a overview
  useEffect(() => {
    if (!tabs.some((t) => t.key === active)) setActive('overview');
  }, [tabs, active]);

  const renderPanel = () => {
    switch (active) {
      case 'overview':
        return <OverviewTab summary={summary} onGoTab={goTab} />;
      case 'profile':
        return <ProfileTab />;
      case 'club':
        return <MyClubTab />;
      case 'purchases':
        return <MyPurchasesTab />;
      case 'subscriptions':
        return <MySubscriptionsTab />;
      case 'events':
        return <MyEventsTab />;
      case 'videos':
        return <MyVideosTab />;
      case 'lives':
        return <MyLivesTab />;
      case 'downloads':
        return <MyDownloadsTab />;
      case 'player':
        return <PlayerProfileTab />;
      default:
        return <OverviewTab summary={summary} onGoTab={goTab} />;
    }
  };

  return (
    <MainLayout>
      <section className="py-8 sm:py-12 lg:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Cabecera */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6 sm:mb-8">
            <div>
              <span className="text-[11px] uppercase tracking-[0.25em] text-[var(--ns-gold-600)] font-semibold">
                Mi cuenta
              </span>
              <h1
                data-testid={DASHBOARD.greeting}
                className="font-display uppercase tracking-tight text-[var(--ns-text)] text-3xl sm:text-4xl lg:text-5xl leading-[0.98] mt-1"
              >
                Hola, {user?.first_name || 'bienvenida'}
              </h1>
              <p className="text-sm sm:text-base text-[var(--ns-text-muted)] mt-2">
                Tu zona privada de NetScouters. Gestiona tu cuenta, compras, contenidos y club favorito.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {isAdmin ? (
                <Button
                  asChild
                  variant="outline"
                  data-testid={DASHBOARD.goAdminButton}
                  className="h-10 px-4 rounded-xl bg-white border-[var(--ns-border-strong)] text-[var(--ns-text)] hover:bg-[var(--ns-amber-50)] hover:border-[var(--ns-gold)]"
                >
                  <Link to={ROUTES.admin} className="inline-flex items-center gap-2 text-sm">
                    <ShieldCheck className="h-4 w-4" />
                    Panel admin
                  </Link>
                </Button>
              ) : null}
              <Button
                variant="outline"
                data-testid={DASHBOARD.logoutButton}
                onClick={logout}
                className="h-10 px-4 rounded-xl bg-white border-[var(--ns-border-strong)] text-[var(--ns-red)] hover:bg-[var(--ns-red)]/8 hover:border-[var(--ns-red)]"
              >
                <LogOut className="h-4 w-4 mr-1.5" />
                Salir
              </Button>
            </div>
          </div>

          {/* Tabs horizontales con scroll en móvil */}
          <div
            data-testid={DASHBOARD.tabsList}
            role="tablist"
            className="mb-6 sm:mb-8 border-b border-[var(--ns-border)]"
          >
            <ScrollArea className="w-full whitespace-nowrap">
              <div className="flex items-center gap-1 pb-0.5">
                {tabs.map((t) => {
                  const Icon = t.icon;
                  const isActive = active === t.key;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => goTab(t.key)}
                      data-testid={DASHBOARD.tab(t.key)}
                      className={[
                        'group inline-flex items-center gap-2 px-3 sm:px-4 py-2.5 text-sm font-medium rounded-t-lg shrink-0 transition-colors',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ns-gold)]/60',
                        isActive
                          ? 'text-[var(--ns-gold-700)] border-b-2 border-[var(--ns-gold)] -mb-px bg-[var(--ns-amber-50)]/60'
                          : 'text-[var(--ns-text-muted)] hover:text-[var(--ns-text)] hover:bg-[var(--ns-bg-2)]/50 border-b-2 border-transparent',
                      ].join(' ')}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      <span>{t.label}</span>
                    </button>
                  );
                })}
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </div>

          {/* Panel activo */}
          <div data-testid={DASHBOARD.panel(active)}>
            {loading && active === 'overview' ? (
              <div className="grid gap-4 sm:gap-6 md:grid-cols-2">
                <Skeleton className="h-44 w-full rounded-2xl" />
                <Skeleton className="h-44 w-full rounded-2xl" />
                <Skeleton className="h-32 w-full rounded-2xl md:col-span-2" />
              </div>
            ) : (
              renderPanel()
            )}
          </div>
        </div>
      </section>
    </MainLayout>
  );
}
